import { useDataSourceColumns } from "@/hooks/data/useDataSourceColumns";
import { useProcessedData } from "@/stores/data-processing";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { logger } from "@/utils/debug";
import { formatNumber } from "@/utils/format";
import { Button, Group, Modal, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useMemo } from "react";

export function WorkspaceModalMapGroup({
  onClose,
  title = "Configure Group",
  initialColumnName,
}: {
  onClose: () => void;
  title?: string;
  initialColumnName?: string | null;
}) {
  const activeDataflowId = useActiveDataflowId();
  const setGroupColumnName = useMapStore((state) => state.setGroupColumnName);
  const groupState = useMapStore(
    (state) => state.groups[activeDataflowId || ""]
  );
  const groupColumnName = groupState?.groupColumnName ?? null;
  const availableColumns = useDataSourceColumns();
  const processedData = useProcessedData(activeDataflowId || "");

  const columnOptions = useMemo(() => {
    if (!processedData?.data || !availableColumns.length) {
      return availableColumns.map((col) => ({
        value: col,
        label: col,
        uniqueCount: 0,
      }));
    }

    const options = availableColumns.map((column) => {
      const uniqueValues = new Set<string>();
      processedData.data.forEach((row) => {
        const value = row[column];
        if (value != null && value !== "") {
          uniqueValues.add(String(value));
        }
      });

      const value = column;
      const uniqueCount = uniqueValues.size;
      const label = `${value} (${formatNumber(uniqueCount)})`;
      const disabled = uniqueCount > processedData.data.length / 2;
      return {
        value,
        label,
        uniqueCount,
        disabled,
      };
    });

    return options.sort((a, b) => a.uniqueCount - b.uniqueCount);
  }, [availableColumns, processedData]);


  const form = useForm<{
    columnName: string | null;
  }>({
    initialValues: {
      columnName:
        initialColumnName !== undefined ? initialColumnName : groupColumnName,
    },
  });

  // Reset form values when modal opens or when initialColumnName/groupColumnName changes
  // This ensures the form always shows the current selected column
  useEffect(() => {
    const columnToUse =
      initialColumnName !== undefined ? initialColumnName : groupColumnName;
    // Only update if the values have actually changed to avoid unnecessary re-renders
    if (form.values.columnName !== columnToUse) {
      form.setValues({
        columnName: columnToUse,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColumnName, groupColumnName]);

  const handleSubmit = (values: { columnName: string | null }) => {
    if (!activeDataflowId) {
      logger.error("Cannot save group config: missing activeDataflowId");
      return;
    }

    let uniqueValuesForChunks: string[] | undefined;
    if (values.columnName && processedData?.data) {
      const uniqueValuesSet = new Set<string>();
      processedData.data.forEach((row) => {
        const groupValue = row[values.columnName!];
        if (groupValue != null && groupValue !== "") {
          const group = String(groupValue).trim();
          uniqueValuesSet.add(group);
        }
      });
      uniqueValuesForChunks = Array.from(uniqueValuesSet).sort();
    }

    setGroupColumnName(
      activeDataflowId,
      values.columnName,
      uniqueValuesForChunks
    );

    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={title}
      size="md"
      closeOnClickOutside={false}
      centered
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, (errors) => {
          logger.error("Form validation errors:", errors);
        })}
      >
        <Stack gap="md">
          <Select
            label="Group Column"
            placeholder="Select column to group by..."
            data={columnOptions}
            {...form.getInputProps("columnName")}
            allowDeselect={true}
            searchable
            clearable
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
